import React, { useState } from 'react';
import { linkLabel, linkKind } from '../../lib/contactIds';
import { appendSummary } from '../../lib/contactSummary';
import { useContactSummary } from '../../hooks/useContactSummary';
import { AlertCircle, Loader2, Sparkles } from 'lucide-react';
import { useContact } from '../../context/ContactContext';
import { useAcwClock } from '../../hooks/useAcwClock';
import { ACW_ALERT_MINUTES } from '../../lib/acw';
import '../../styles/wrapup-notes.css';
// v3 design parity (#114, epic #117): markup and styles only, scoped to
// .wrapup-v3-screen. Imported after wrapup-notes.css so its rules win ties.
import '../../styles/wrapup-v3.css';

// Kept in lockstep with the DISPOSITIONS set in lambdas/knktr-agent-wrapup —
// the backend validates against its own copy and 400s on anything not in it,
// so adding an option here alone would fail the save.
const DISPOSITIONS = [
  'Resolved',
  'Follow-up required',
  'Callback scheduled',
  'Requires client action',
  // Synthetic/test contacts (e.g. StartTaskContact console verification runs).
  // Exists so testing never has to pick a misleading real disposition and
  // pollute the wrap-up record with something that reads as genuine work.
  'Test',
];

// After Call Work wrap-up. This panel is the ACW gate: the contact is held
// open Connect-side (never auto-cleared) until Save & Complete persists the
// wrap-up successfully. No save, no complete.
export default function WrapUp() {
  // D28: notes come from context, pre-filled with what the agent typed during
  // the call; edits here write back to the same field.
  const { display, wrapContext, wrapSaving, wrapError, submitWrapUp, callNotes, setCallNotes, wrapNotesAiSeeded, wrapCallback } = useContact();

  const [disposition, setDisposition] = useState('');
  const [flagForClient, setFlagForClient] = useState(false);
  // 2026-09-17: agent-scheduled callback (vault #133). Booking Lambda is the
  // single source of truth for business-hours/timezone clamping — this
  // picker only bounds the OBVIOUS cases (no past dates, weekdays only) so
  // an agent isn't led to expect a Saturday 6pm callback that will silently
  // become Monday 9am; the confirmed window comes back after Save.
  const [callbackDate, setCallbackDate] = useState('');
  const [callbackTime, setCallbackTime] = useState('');
  // 2026-09-21 (vault #191): who the callback comes back to. Default is the
  // queue — the same behaviour as before this existed. "Assigned to me" is a
  // request: the wrap-up Lambda resolves it against the agent's own quick
  // connect and falls back to the queue if they aren't provisioned, so this
  // can never cost the customer their callback.
  const [callbackAssignToMe, setCallbackAssignToMe] = useState(false);
  // #24 (2026-09-23, the confirmed-live callback-retry loop): ticked only
  // when the agent has explicitly confirmed a 3rd+ booking in an existing
  // chain. The server gate (lib/callback.mjs assessChainDepth) is the real
  // boundary — this only lets a deliberate override reach it.
  const [chainOverrideConfirmed, setChainOverrideConfirmed] = useState(false);
  const isCallbackDisposition = disposition === 'Callback scheduled';
  // 0 if this contact was never itself part of a callback chain. A save from
  // here would be booking #(chainDepth + 1) in that chain.
  const wouldBeChainDepth = (display?.chainDepth || 0) + 1;
  // The server already refused once (a save was attempted and came back
  // gated) — same confirm step, so a stale client-side depth read never
  // silently blocks the agent from finishing.
  const serverRequiresOverride = !!wrapCallback?.requiresOverride;
  const chainNeedsOverride = (wouldBeChainDepth >= 3 || serverRequiresOverride) && !chainOverrideConfirmed;
  const callbackDateObj = callbackDate ? new Date(`${callbackDate}T00:00:00`) : null;
  const callbackIsWeekend = callbackDateObj && [0, 6].includes(callbackDateObj.getDay());
  const callbackReady = !isCallbackDisposition || (!!callbackDate && !!callbackTime && !callbackIsWeekend && !chainNeedsOverride);

  // Choosing "Requires client action" implies the client flag.
  const dispositionForcesFlag = disposition === 'Requires client action';
  const clientActionRequired = flagForClient || dispositionForcesFlag;

  const contactId = wrapContext?.contactId ?? display?.meta?.contactId ?? '—';
  // D31: the id above is the CONNECTED leg (what the API binds on); the chain
  // root of a re-offered callback is shown alongside so the agent can see why
  // it differs from the id the earlier offers displayed.
  const chainRoot = wrapContext?.initialContactId ?? display?.meta?.initialContactId ?? null;
  const reoffered = Boolean(chainRoot && contactId && chainRoot !== contactId);
  // Row 46: a linked leg is a transfer or a re-offered callback; say which.
  const linkMethod = wrapContext?.initiationMethod ?? display?.meta?.initiationMethod ?? null;
  const linkIsTransfer = linkKind(linkMethod) === 'transfer';
  const brand = wrapContext?.brandName ?? display?.brandName ?? null;
  const customerName = display?.customer?.name ?? display?.customer?.phone ?? 'Caller';
  const escReason = display?.escalation?.reason ?? null;

  const canSave = !!disposition && !wrapSaving && callbackReady;

  // D32 (2026-09-14): time in after-call work, counting from the first second
  // (Storm: never hidden before the threshold), urgent at ACW_ALERT_MINUTES —
  // the same point the server-side sweeper alerts. While this is unsaved the
  // agent cannot receive anything on any channel.
  const clock = useAcwClock(wrapContext?.acwStartedAt);

  // 2026-09-15: the notes box opens with the bot leg's AI summary when the
  // agent typed nothing live (seeded in ContactContext at CONTACT_ACW, never
  // over the agent's own words). While the text is still exactly that
  // summary, say so — these notes go on record, and the agent should know at
  // a glance they did not start as their words. The cue stays until Save
  // (Storm, 2026-09-15): editing does not change where the text began.
  // The wording states the summary's real scope: buildSummary() in
  // knktr-bedrock-rag-dev runs ONCE at the moment of escalation over the
  // caller–AI log up to that point (what the caller wanted, what the AI
  // answered, why escalating) and is never regenerated after the agent
  // joins — so the agent's own part of the call is always missing from it.
  const aiSeededBotOnly = Boolean(wrapNotesAiSeeded && wrapContext?.aiSummary);

  // Backlog 125 (Storm 2026-10-21): one summary of the WHOLE call, both parts
  // clearly marked. Fetch and seeding rules live in hooks/useContactSummary.js.
  const summary = useContactSummary({
    contactId: wrapContext?.contactId ?? null,
    botSummary: wrapContext?.aiSummary ?? null,
    callNotes,
    setCallNotes,
  });
  const summarySeeded = summary.status === 'ready' && summary.seeded;
  const aiSeeded = summarySeeded || aiSeededBotOnly;
  const offerAddSummary = summary.status === 'ready' && !summary.seeded && !String(callNotes || '').includes(summary.text);

  // v3 (#114): the spec's line beside Save, saying why it's disabled. Derived
  // from the same values canSave uses; no new state.
  const saveHint = wrapSaving ? ''
    : !disposition ? 'Pick a disposition to save.'
    : chainNeedsOverride ? `Confirm the booking #${wouldBeChainDepth} checkbox above to save.`
    : !callbackReady ? 'Pick a weekday date and a time for the callback.'
    : 'Saving completes the contact. You can receive contacts again once it saves.';

  function handleSave() {
    if (!canSave) return;
    submitWrapUp({
      notes: callNotes,
      disposition,
      clientActionRequired,
      ...(isCallbackDisposition ? { callback: { date: callbackDate, time: callbackTime, assignToMe: callbackAssignToMe } } : {}),
      // #24: only ever sent once the agent has ticked the confirm below.
      ...(isCallbackDisposition && chainOverrideConfirmed ? { overrideChainWarning: true } : {}),
    });
  }

  return (
    <main className="centre-panel wrapup-panel wrapup-v3-screen">
      <div className="wrapup-card">

        <div className="wrapup-head">
          <div>
            <h2 className="wrapup-title">Wrap up this contact</h2>
            <p className="wrapup-sub">
              Complete this to finish the contact. It stays open until saved.
            </p>
          </div>
        </div>

        <div className={`wrapup-clock${clock.urgent ? ' urgent' : ''}`} role={clock.urgent ? 'alert' : undefined}>
          <span className="wrapup-clock-time">{clock.text}</span>
          <span className="wrapup-clock-note">
            {clock.urgent
              ? `Unsaved for over ${ACW_ALERT_MINUTES} minutes — you can’t receive calls, tasks or chats until this is saved.`
              : 'in wrap-up — nothing can reach you until this is saved.'}
          </span>
        </div>

        {/* Context recap so the agent isn't guessing which contact this is */}
        <div className="wrapup-context">
          <div className="wrapup-ctx-item">
            <span className="wrapup-ctx-key">Customer</span>
            <span className="wrapup-ctx-val">{customerName}</span>
          </div>
          {brand && (
            <div className="wrapup-ctx-item">
              <span className="wrapup-ctx-key">Tenant</span>
              <span className="wrapup-ctx-val">{brand}</span>
            </div>
          )}
          <div className="wrapup-ctx-item">
            <span className="wrapup-ctx-key">Contact ID</span>
            <span className="wrapup-ctx-val mono">{contactId}</span>
          </div>
          {reoffered && (
            <div className="wrapup-ctx-item">
              <span className="wrapup-ctx-key">{linkIsTransfer ? 'Transferred from' : 'Chain root'}</span>
              <span className="wrapup-ctx-val mono" title={linkIsTransfer ? 'This call was transferred; the first leg of the call' : 'The original contact of the chain'}>{String(chainRoot).slice(0, 8)}… ({linkLabel(linkMethod)})</span>
            </div>
          )}
        </div>
        {escReason && (
          <div className="wrapup-esc-note">
            Escalated because: {escReason}
          </div>
        )}

        {/* Disposition. v3 (#114): the spec's one-click chips instead of a
            dropdown. Real radio inputs, so it stays a keyboard radio group;
            same values, same setDisposition. */}
        <div className="wrapup-field">
          <span className="wrapup-label" id="wrapup-disposition-label">Disposition <span className="req">required</span></span>
          <div className="wrapup-disposition" role="radiogroup" aria-labelledby="wrapup-disposition-label">
            {DISPOSITIONS.map(d => (
              <label key={d} className={`wrapup-chip${disposition === d ? ' is-on' : ''}`}>
                <input
                  type="radio"
                  name="wrapup-disposition"
                  value={d}
                  checked={disposition === d}
                  onChange={() => setDisposition(d)}
                />
                {d}
              </label>
            ))}
          </div>
        </div>

        {/* 2026-09-17: agent-scheduled callback (vault #133). Only shown for
            the one disposition it means anything for. Bounds are a courtesy,
            not the source of truth — knktr-callback-booking clamps for real
            (business hours, weekdays, up to ~6 days out) and the post-save
            outcome shows what was actually booked. */}
        {isCallbackDisposition && (
          <div className="wrapup-field wrapup-callback">
            <span className="wrapup-label">
              Callback date &amp; time <span className="req">required</span>
            </span>
            <div className="wrapup-callback-picker">
              <input
                type="date"
                className="wrapup-select"
                value={callbackDate}
                min={new Date().toISOString().slice(0, 10)}
                onChange={e => setCallbackDate(e.target.value)}
              />
              <input
                type="time"
                className="wrapup-select"
                value={callbackTime}
                onChange={e => setCallbackTime(e.target.value)}
              />
            </div>
            <div className="wrapup-callback-owner" role="radiogroup" aria-label="Who takes this callback">
              <label className={`wrapup-owner-opt${callbackAssignToMe ? '' : ' is-on'}`}>
                <input
                  type="radio"
                  name="callback-owner"
                  checked={!callbackAssignToMe}
                  onChange={() => setCallbackAssignToMe(false)}
                />
                Anyone available
              </label>
              <label className={`wrapup-owner-opt${callbackAssignToMe ? ' is-on' : ''}`}>
                <input
                  type="radio"
                  name="callback-owner"
                  checked={callbackAssignToMe}
                  onChange={() => setCallbackAssignToMe(true)}
                />
                Assigned to me
              </label>
            </div>
            {callbackAssignToMe && (
              <span className="wrapup-callback-hint">
                Comes back to you. If your console account isn't set up for personal
                callbacks yet, it goes to the queue instead — you'll see which on save.
              </span>
            )}
            {callbackIsWeekend && (
              <span className="wrapup-callback-hint warn">
                That's a weekend — the booking only schedules weekdays 9am–4:30pm Sydney time and will move this to the next business day.
              </span>
            )}
            {/* #24 (2026-09-23): 2nd booking in a chain — informational, never
                blocks Save. Read straight off this contact's own attributes
                (display.chainDepth), stamped by the booking Lambda; #20's
                stamp is what makes it present on a voice leg too, not just a
                task. */}
            {wouldBeChainDepth === 2 && (
              <span className="wrapup-callback-hint warn" role="status">
                This customer already has a callback scheduled from an earlier attempt.
                This will be booking #2 for them.
              </span>
            )}
            {/* 3rd+: the real boundary is server-side (the save is refused,
                surfaced below via serverRequiresOverride, without losing the
                agent's notes); this is the confirm step that lets a
                deliberate resubmit through. */}
            {(wouldBeChainDepth >= 3 || serverRequiresOverride) && (
              <label className="wrapup-callback-hint warn wrapup-chain-override">
                <input
                  type="checkbox"
                  checked={chainOverrideConfirmed}
                  onChange={e => setChainOverrideConfirmed(e.target.checked)}
                />
                This will be booking #{wouldBeChainDepth} for this customer — an unusual number
                of callback attempts. Confirm to book anyway.
              </label>
            )}
            {!callbackIsWeekend && (
              <span className="wrapup-callback-hint">
                Business hours (9am–4:30pm Sydney, weekdays) are enforced by the booking system — you'll see the confirmed window after saving.
              </span>
            )}
          </div>
        )}

        {/* Notes */}
        <label className="wrapup-field">
          <span className="wrapup-label">Notes</span>
          {summarySeeded ? (
            <span className="wrapup-notes-cue" id="wrapup-notes-cue">
              <Sparkles size={12} />
              <strong>AI-drafted</strong> — a summary of the whole call: the AI assistant's part and yours. Check it and correct anything wrong, then save.
            </span>
          ) : aiSeededBotOnly && (
            <span className="wrapup-notes-cue" id="wrapup-notes-cue">
              <Sparkles size={12} />
              <strong>AI-suggested</strong> — covers only what happened before you joined. Add what you did, then save.
            </span>
          )}
          {summary.status === 'loading' && (
            <span className="wrapup-summary-status" role="status">
              <Loader2 size={12} className="spin" /> Writing a summary of the whole call…
            </span>
          )}
          {offerAddSummary && (
            <button
              type="button"
              className="wrapup-summary-add"
              onClick={e => { e.preventDefault(); setCallNotes(appendSummary(callNotes, summary.text)); }}
            >
              <Sparkles size={12} /> Add the AI summary of the whole call below your notes
            </button>
          )}
          <textarea
            className={`wrapup-textarea${aiSeeded ? ' ai-seeded' : ''}${summarySeeded ? ' summary-seeded' : ''}`}
            placeholder="What happened on this call, what you did, and any follow-up…"
            value={callNotes}
            onChange={e => setCallNotes(e.target.value)}
            aria-describedby={aiSeeded ? 'wrapup-notes-cue' : undefined}
          />
        </label>

        {/* Flag for client */}
        <label className={`wrapup-flag ${clientActionRequired ? 'on' : ''}`}>
          <input
            type="checkbox"
            checked={clientActionRequired}
            disabled={dispositionForcesFlag}
            onChange={e => setFlagForClient(e.target.checked)}
          />
          <span>
            Flag for client
            {dispositionForcesFlag && (
              <span className="wrapup-flag-hint"> — implied by “Requires client action”</span>
            )}
          </span>
        </label>

        {wrapError && (
          <div className="wrapup-error">
            <AlertCircle size={14} />
            <span>{wrapError}</span>
          </div>
        )}

        <div className="wrapup-save-row">
          <button
            className="wrapup-save"
            onClick={handleSave}
            disabled={!canSave}
            title={!disposition ? 'Select a disposition first' : 'Save & Complete'}
          >
            {wrapSaving ? (
              <>
                <Loader2 size={15} className="spin" />
                Saving…
              </>
            ) : (
              <>Save &amp; Complete</>
            )}
          </button>
          {saveHint && <span className="wrapup-save-hint">{saveHint}</span>}
        </div>

      </div>
    </main>
  );
}
